// Heavy legacy-mode integration path. Dynamically imported from src/index.js
// only when the slim initial-payment-calc flag is OFF — keeping the entry
// bundle minimal when slim mode is enabled.

import { initWalletManager } from './events/initWalletManager.js';
import { activateWalletEventListeners as activateWalletEventListenersLegacy } from './listeners/index.legacy.js';
import { insertDealStarter as insertDealStarterLegacy } from './placements/dealStarter.legacy.js';
import { insertWalletBonnet as insertWalletBonnetLegacy } from './placements/walletBonnet.legacy.js';
import { insertWalletModal as insertWalletModalLegacy } from './placements/walletModal.legacy.js';
import { insertWalletGrid as insertWalletGridLegacy } from './placements/walletGrid.legacy.js';
import { insertSearchBudget as insertSearchBudgetLegacy } from './placements/walletSearchBudget.legacy.js';
import { insertTestDriveCTA as insertTestDriveCTALegacy } from './placements/vehicleCtas.legacy.js';
import { insertHeaderWallet as insertHeaderWalletLegacy } from './placements/walletHeader.legacy.js';
import { loadCSS } from './css/index.js';
import { loadWalletManager as loadWalletManagerLegacy } from './events/loadWalletManager.legacy.js';
import { getTenant, isSectionsVDP } from './helpers/index.js';
import { ddcLog } from './utils/logger.js';

/**
 * Run the full legacy-mode integration after the lightweight entry-point
 * setup has resolved config, vehicles, dealer data, and DR Admin.
 *
 * @param {Object} API - DDC APILoader instance
 * @param {Object} ctx - Everything the heavy path needs from the entry point
 */
export async function runFullLegacyIntegration(API, ctx) {
	const {
		config,
		pageData,
		vehicles,
		drAdminProducts,
		drAdminConfigRoot,
		walletScriptAssetsHost,
		walletManagerHost,
		formatDownPaymentPercent,
		isSRP,
		isMobile,
		siteId,
		detailPage,
		indexPage,
		useStaging,
		enableDealStarter,
		showHeaderPlacement,
		featureTogglesPromise,
		udsPromise,
		theme,
	} = ctx;

	ddcLog('Loading CSS with config and pageData', { config, pageData, theme });
	loadCSS(config, pageData);

	activateWalletEventListenersLegacy(API, isSRP, config, vehicles, indexPage, siteId, walletScriptAssetsHost, detailPage);

	ddcLog('Inserting wallet bonnet and loading wallet manager...');
	const [featureToggles] = await Promise.all([
		featureTogglesPromise,
		insertWalletBonnetLegacy(API, walletScriptAssetsHost),
		loadWalletManagerLegacy(API, walletManagerHost),
	]);

	ddcLog('Initializing wallet manager...');
	initWalletManager({
		useStaging,
		downPaymentPercent: formatDownPaymentPercent,
		tenant: getTenant(config),
		featureToggles,
		drSettings: drAdminConfigRoot,
	});

	// Wait for the UDS bundle (kicked off earlier, in parallel) to finish loading...
	const initUds = await udsPromise;
	// ...then run it now that wallet event listeners are wired up and can respond.
	await initUds();

	ddcLog('Inserting wallet modal...');
	const placementPromises = [insertWalletModalLegacy(API, walletScriptAssetsHost, config, vehicles, drAdminProducts?.hasLeasing)];

	if (showHeaderPlacement) {
		ddcLog('Inserting header wallet...');
		const { mywalletIcon } = config;
		insertHeaderWalletLegacy(API, isMobile, mywalletIcon);
	}

	let pageTypeHandled = true;
	if (isSRP) {
		ddcLog('Inserting wallet grid for SRP...');
		placementPromises.push(insertWalletGridLegacy(API, walletScriptAssetsHost, config));
	} else if (detailPage) {
		if (!isSectionsVDP()) {
			ddcLog('Inserting test drive CTA...');
			insertTestDriveCTALegacy(API, config);
		}
		if (enableDealStarter) {
			placementPromises.push(insertDealStarterLegacy(API, walletScriptAssetsHost, config));
		}
	} else if (indexPage) {
		ddcLog('Inserting index page placements...');
		const {
			searchByBudget,
			showSiteTextSearchPlacement,
			showFacetBrowsePlacement,
			facetBrowseLinkColor,
			siteTextSearchLinkColor,
		} = config;
		if (showSiteTextSearchPlacement) {
			insertSearchBudgetLegacy(API, { searchByBudget, linkColorConfig: siteTextSearchLinkColor }, 'site-text-search');
		}
		if (showFacetBrowsePlacement) {
			insertSearchBudgetLegacy(API, { searchByBudget, linkColorConfig: facetBrowseLinkColor }, 'facet-browse');
		}
	} else {
		pageTypeHandled = false;
	}

	await Promise.all(placementPromises);

	if (!pageTypeHandled) {
		API.log('Not SRP, detail or index page. Aborting.');
		return;
	}

	ddcLog('Integration completed successfully (legacy mode)');
}

export default runFullLegacyIntegration;
